import { ReactNode, useRef, useState } from "react";
import {
  Badge,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
  CircularProgress,
  IconButton,
  Popover,
  Switch,
} from "@mui/material";
import { ArrowBack, Cancel, CheckCircle, MoreHoriz } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getPlanningStatus } from "@utils";
import {
  useWebApiV1UpdateOrderMutation,
  useWebApiV1OrderQuery,
  OrderOut,
  useWebApiV1GetUserFeatureSwitchesQuery,
  WebApiV1UpdateOrderApiArg,
} from "@providers/hop-ord-server/api";
import { Feature, planningStatusColourMapping, StructureStatus } from "@enums";
import PageHeader from "./PageHeader";
import { PAGE_HEADER_HEIGHT } from "@constants";

interface PageLeftHandSidebarProps {
  updateOrder: (order: WebApiV1UpdateOrderApiArg) => void;
  orderLoading: boolean;
  orderData: OrderOut | undefined;
  currentPageIndex: number;
  orderId: number;
}

interface Props {
  orderId: number;
  currentPageIndex: number;
  children: ReactNode;
}

const PageLeftHandSidebar = ({
  currentPageIndex,
  orderLoading,
  orderData,
  updateOrder,
}: PageLeftHandSidebarProps) => {
  const ctExists = orderData?.ctStatus === StructureStatus.COMPLETE;
  const rtStructExists = orderData?.rtstructStatus === StructureStatus.COMPLETE;
  const isTestCase = orderData?.isTest;
  const orderId = orderData?.id;

  const navigate = useNavigate();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const theme = useTheme();

  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();

  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  const navigationItems = showPlanReview
    ? ["Submit Careplan", "Plan Generation", "Plan Review", "Handover"]
    : ["Submit Careplan", "Plan Generation", "Handover"];

  const planningStatus = getPlanningStatus(
    orderData?.status,
    ctExists,
    rtStructExists,
    orderData?.handoverDatetime !== null,
  );

  return (
    <Stack
      width={"240px"}
      minWidth={"240px"}
      height={1}
      bgcolor={"customBackground.main"}
      borderRight={1}
      borderColor={"divider"}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        onClick={() => navigate("/commandcenter")}
        sx={{
          cursor: "pointer",
          padding: 1,
          borderBottom: 1,
          borderColor: "divider",
          height: PAGE_HEADER_HEIGHT,
        }}
      >
        <IconButton>
          <ArrowBack />
        </IconButton>
        <Stack>
          <Typography variant="body1">Back</Typography>
          <Typography color={"text.secondary"} variant="body1">
            Command Center
          </Typography>
        </Stack>
      </Stack>
      <Stack paddingX={2} paddingY={1} gap={3}>
        {planningStatus && (
          <Stack>
            <Typography variant="body1">Planning Status</Typography>
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              justifyContent="flex-start"
              paddingLeft={1}
            >
              <Badge
                color={planningStatusColourMapping[planningStatus]}
                badgeContent={<></>}
                slotProps={{
                  badge: {
                    sx: { height: 16, minWidth: 16 },
                  },
                }}
              ></Badge>
              <Typography variant="body2" color={"text.primary"}>
                {planningStatus}
              </Typography>
            </Stack>
          </Stack>
        )}
        <Stack gap={0.5}>
          <Typography variant="body1">CT & RT Struct</Typography>
          <Chip
            color={orderLoading ? "secondary" : ctExists ? "success" : "error"}
            sx={{ width: "fit-content" }}
            icon={
              orderLoading ? (
                <CircularProgress size={18} />
              ) : ctExists ? (
                <CheckCircle />
              ) : (
                <Cancel />
              )
            }
            variant="outlined"
            label="CT Exists"
          />
          <Chip
            color={
              orderLoading ? "secondary" : rtStructExists ? "success" : "error"
            }
            sx={{ width: "fit-content" }}
            icon={
              orderLoading ? (
                <CircularProgress size={18} />
              ) : rtStructExists ? (
                <CheckCircle />
              ) : (
                <Cancel />
              )
            }
            variant="outlined"
            label="RTStruct Exists"
          />
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 1 }} />
      <Stack
        paddingX={2}
        gap={3}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isTestCase ? (
          <Chip
            label="Test Case"
            color="info"
            variant="outlined"
            sx={{ width: "fit-content" }}
          />
        ) : (
          <Chip
            label="Clinical Case"
            color="success"
            variant="outlined"
            sx={{ width: "fit-content" }}
          />
        )}
        <IconButton
          ref={anchorRef}
          onClick={() => setShowMoreOptions((prevOpen) => !prevOpen)}
          data-testid="pop-edit-button"
        >
          <MoreHoriz />
        </IconButton>
      </Stack>
      <Divider sx={{ marginY: 1 }} />
      <Stack>
        <List sx={{ width: 1 }} disablePadding>
          {navigationItems.map((item, idx) => (
            <ListItem
              key={idx}
              data-testid={`site-group-${idx}`}
              sx={{
                ...(idx == currentPageIndex
                  ? {
                      backgroundColor: "secondary.light",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }
                  : {
                      borderLeft: `4px solid transparent`,
                    }),
              }}
            >
              <Stack
                direction="row"
                gap={3}
                alignItems="center"
                justifyContent="flex-start"
                paddingLeft={1}
              >
                {idx < currentPageIndex ? (
                  <Badge
                    badgeContent={
                      <CheckCircle color="primary" sx={{ padding: 0 }} />
                    }
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "transparent",
                        color: "primary.main",
                      },
                    }}
                  ></Badge>
                ) : (
                  <Badge badgeContent={idx + 1} color="primary"></Badge>
                )}
                <Typography variant="body1">{item}</Typography>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Stack>
      <Popover
        id={"more-options-popover"}
        open={showMoreOptions}
        anchorEl={anchorRef.current}
        onClose={() => setShowMoreOptions(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography sx={{ p: 2 }}>
          Test case
          <Switch
            checked={isTestCase}
            onChange={() => {
              updateOrder({
                orderId: Number(orderId),
                orderIn: {
                  isTest: !isTestCase,
                },
              });
            }}
          />
        </Typography>
      </Popover>
    </Stack>
  );
};

const WorkflowPageWrapper = ({
  children,
  orderId,
  currentPageIndex,
}: Props) => {
  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const { data: order, isLoading } = useWebApiV1OrderQuery({ orderId });

  return (
    <Stack direction="row" height={1} width={1}>
      <PageLeftHandSidebar
        updateOrder={updateOrder}
        orderLoading={isLoading}
        orderData={order}
        currentPageIndex={currentPageIndex}
        orderId={orderId}
      />
      <Stack flexGrow={1} minWidth={0}>
        <PageHeader orderData={order} />
        <Stack flexGrow={1} overflow="auto">
          {children}
        </Stack>
        <Box id="bottom-action-bar" />
      </Stack>
    </Stack>
  );
};

export default WorkflowPageWrapper;
